import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import DashboardLayout from '../components/DashboardLayout';

interface FormData {
  name_en: string;
  name_ar: string;
  slug: string;
  order: number;
  active: boolean;
  description: string;
  description_ar: string;
  icon: string;
}

export default function IngredientCategoryForm() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name_en: '',
    name_ar: '',
    slug: '',
    order: 0,
    active: true,
    description: '',
    description_ar: '',
    icon: '',
  });

  useEffect(() => {
    if (isEditing && id) loadCategory();
  }, [id]);

  const loadCategory = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await api.get(`/ingredients/categories/${id}`);
      const u = res.data?.data ?? res.data;
      setFormData({
        name_en: u.name_en ?? '',
        name_ar: u.name_ar ?? '',
        slug: u.slug ?? '',
        order: u.order ?? 0,
        active: u.active !== false,
        description: u.description ?? '',
        description_ar: u.description_ar ?? '',
        icon: u.icon ?? '',
      });
    } catch (err) {
      console.error(err);
      alert(t('pantryCategories.failedToLoad'));
      navigate('/?tab=pantryCategories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        name_en: formData.name_en.trim(),
        name_ar: formData.name_ar.trim(),
        slug: formData.slug.trim() || formData.name_en.toLowerCase().replace(/\s+/g, '-'),
        order: formData.order,
        active: formData.active,
        description: formData.description.trim() || undefined,
        description_ar: formData.description_ar.trim() || undefined,
        icon: formData.icon.trim() || undefined,
      };
      if (isEditing && id) {
        await api.patch(`/ingredients/categories/${id}`, payload);
      } else {
        await api.post('/ingredients/categories', payload);
      }
      navigate('/?tab=pantryCategories');
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || t('pantryCategories.failedToUpdate'));
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditing) {
    return (
      <DashboardLayout title={t('common.loading')}>
        <div className="flex items-center justify-center min-h-[300px]">{t('pantryCategories.loading')}</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={isEditing ? t('pantryCategories.editTitle') : t('pantryCategories.createTitle')}
      subtitle={t('pantryCategories.subtitle')}
    >
      <div className="max-w-2xl mx-auto">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('pantryCategories.nameEn')}</label>
            <input
              type="text"
              required
              value={formData.name_en}
              onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('pantryCategories.nameAr')}</label>
            <input
              type="text"
              value={formData.name_ar}
              onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              dir="rtl"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('pantryCategories.slug')}</label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              placeholder="e.g. dairy"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('pantryCategories.order')}</label>
            <input
              type="number"
              min={0}
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value, 10) || 0 })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('pantryCategories.descriptionEn')}</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('pantryCategories.descriptionAr')}</label>
            <textarea
              value={formData.description_ar}
              onChange={(e) => setFormData({ ...formData, description_ar: e.target.value })}
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              dir="rtl"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('pantryCategories.icon')}</label>
            <input
              type="text"
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              placeholder="emoji or icon name"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="cat-active"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              className="rounded border-gray-300 text-primary-orange"
            />
            <label htmlFor="cat-active" className="text-sm font-medium text-gray-700">{t('pantryCategories.active')}</label>
          </div>
          <div className="flex flex-wrap justify-end gap-4 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate('/?tab=pantryCategories')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
            >
              {t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
